import { JOB_POST_REQUIREMENT_DISPLAY_LABEL_MAX_LENGTH } from "@api/shared-runtime/job-post-requirements";

/**
 * Oxford-comma "or" join for requirement display labels.
 * 1 item  → "Physician"
 * 2 items → "Physician or Nurse Practitioner"
 * 3+ items → "Physician, Nurse Practitioner, or Physician Assistant"
 */
function friendlyJoin(names: string[]): string {
  if (names.length === 0) {
    return "";
  }
  if (names.length === 1) {
    return names[0];
  }
  if (names.length === 2) {
    return `${names[0]} or ${names[1]}`;
  }
  return `${names.slice(0, -1).join(", ")}, or ${names[names.length - 1]}`;
}

/**
 * Builds the provider-facing label for a professional-type or specialty
 * requirement, e.g. "Must specialize in Cardiology or Neurology". The API
 * rejects labels over the display-label limit, so when many values are
 * selected, list as many names as fit and summarize the rest
 * ("…, or 6 more"). The requirement itself still stores every selected value.
 */
export function buildRequirementListLabel({
  prefix,
  names,
}: {
  prefix: string;
  names: string[];
}): string {
  const fullLabel = `${prefix} ${friendlyJoin(names)}`;
  if (fullLabel.length <= JOB_POST_REQUIREMENT_DISPLAY_LABEL_MAX_LENGTH) {
    return fullLabel;
  }

  let label = fullLabel;
  for (let shownCount = names.length - 1; shownCount >= 1; shownCount--) {
    const shownNames = names.slice(0, shownCount).join(", ");
    label = `${prefix} ${shownNames}, or ${names.length - shownCount} more`;
    if (label.length <= JOB_POST_REQUIREMENT_DISPLAY_LABEL_MAX_LENGTH) {
      return label;
    }
  }
  return label.slice(0, JOB_POST_REQUIREMENT_DISPLAY_LABEL_MAX_LENGTH);
}
